// Reader-language switch, in place — the landing changes language without a visible page load.
//
// The selector (the named buttons at the top of the card) stays a PLAIN LINK to /{lang} in the
// markup, and that is deliberate: a crawler follows it, a middle-click opens it, and with this
// file missing or broken it simply navigates the old way. All this script does is get in front of
// the click, fetch the same URL the link points at, and move the parts of the page that differ —
// leaving the backdrop, the running garden animation and the scroll position exactly where they
// were.
//
// Why it is worth a file at all (owner, 2026-08-31: "can we avoid (visible) page reload and
// shifting?"): the whole page is server-rendered per locale, so switching language meant a full
// navigation — a white flash, the backdrop image repainting, the garden restarting from a bare
// mound, and the card resizing as the new language's copy landed. Only the last of those is a
// CSS problem (landing.css pins the card's width); the rest are what this file removes.
//
// The parts that differ are enumerated below rather than discovered, because the alternative —
// swapping <main> wholesale — throws away the two decorations mid-animation, which is the exact
// jump the change exists to prevent.
(function () {
    "use strict";

    // Every region whose contents are reader-language-dependent, matched BY INDEX between the
    // live page and the fetched one: same template, same order, so index-matching is exact and
    // needs no ids. Anything not listed here is identical in every language (the backdrop, the
    // garden strip's plants, the card frame) and is never touched.
    var REGIONS = [
        ".landingReaderChoiceClass",   // the reader-language selector (its own ON state moves)
        ".landingEntryHeadClass",      // the rotating headword: the OFFER can differ per page
        ".landingEntryPosClass",
        ".landingEntryGlossClass",     // also carries data-gloss, which the garden reads live
        ".landingTitleClass",
        // ⚠️ The lead's WRAPPER, not the lead itself (2026-10-02): a pair page carries a proof row
        // under the lead and a reader landing does not, so the two are swapped as one block —
        // the same reason the footer below is listed whole. Listing .landingSubClass alone would
        // leave /de/en's German numbers standing under /en's English lead.
        ".landingLeadClass",
        ".landingChoiceClass",         // heading + the two micro lines + the CTA forms
        ".landingGardenCaptionClass",
        ".landingPillarClass",         // four of them
        // ⚠️ The bottom strip is TWO things: the legal links AND the tips link beside them, whose
        // label and href are both reader-language-dependent — and which is ABSENT for a reader
        // language with no tips bank (DE, FR, ES). Swapping the strip's innerHTML carries it
        // along; listing the link separately would not, because a region that exists on /en and
        // not on /de fails sameShape and sends every such switch back through a full page load.
        ".landingFootClass"
    ];

    var SELECTOR = "a.landingReaderBtnClass";

    if (!window.fetch || !window.DOMParser || !window.history || !history.pushState) { return; }

    // Which link the visitor arrived by (docs/project/ad-attribution.md) rides on the address as
    // utm_campaign plus the ad platform's click id, and the server writes it into every CTA href
    // it renders. A language swap fetches ANOTHER landing and swaps its CTA row in, so the fetched
    // address has to carry the same marker or the swap would silently drop it — and the pushed
    // history entry carries it too, so a reload lands where the visitor really is. Only the names
    // the server knows travel; the rest of this page's query string stays where it is.
    //
    // ⛔ Only the CLICK carries it, never the prefetch below. Every fetched landing is a recorded
    // page request, and a marked one counts as a LANDING in the console's campaign funnel — so a
    // marked prefetch of the four other languages would turn one ad click into five landings
    // (measured 2026-09-29 before this line existed: /de/en plus /de, /en, /ru, /es, /fr, all with
    // the label). A marked visitor's first switch therefore misses the prefetch cache and pays one
    // round trip; the dip covers most of it, and ad visitors who switch language are rare.
    // The names Attribution.java knows (SOURCE_PARAM, CAMPAIGN_PARAM, CLICK_ID_PARAMS) — keep the
    // three lists equal. utm_source rides along so every page can tell the platform (§9.3).
    var ATTRIBUTION = ["utm_source", "utm_campaign", "gclid", "gbraid", "wbraid", "msclkid", "rdt_cid"];
    // An AI assistant that linked here with only its Referer has no marker on the address; the
    // server read it and put it on <html data-arrival> (ad-attribution.md §10). The address wins.
    var ARRIVAL = new URLSearchParams(document.documentElement.getAttribute("data-arrival") || "");

    function withAttribution(href) {
        var here = new URLSearchParams(window.location.search);
        var url = new URL(href, window.location.origin);
        ATTRIBUTION.forEach(function (name) {
            var value = here.get(name) || ARRIVAL.get(name);
            if (value && !url.searchParams.has(name)) { url.searchParams.set(name, value); }
        });
        return url.pathname + url.search;
    }

    var parser = new DOMParser();
    var cache = {};        // href -> Promise<parsed document>: switching back and forth is
                           // instant, and a click landing mid-prefetch joins the same request
                           // instead of fetching twice. A failure evicts itself so the click
                           // path can retry (and, failing again, navigate).
    var busy = false;

    // ⚠️ The two documents must have the SAME SHAPE for an index-matched swap to be honest. Every
    // page this file switches between is now the same template with the same blocks — a pair page
    // differs only in how many CTA forms sit inside .landingChoiceClass, which is one region
    // swapped whole — so a mismatch is not expected any more. The check stays because it is what
    // makes a future page with a block of its own DEGRADE instead of leaving the previous
    // language's text on screen: a count that does not match is not a case to patch up, it is a
    // case this enhancement does not cover — so it navigates, exactly as with the script absent.
    function sameShape(doc) {
        return REGIONS.every(function (sel) {
            return document.querySelectorAll(sel).length === doc.querySelectorAll(sel).length;
        });
    }

    function swapRegions(doc) {
        REGIONS.forEach(function (sel) {
            var live = document.querySelectorAll(sel);
            var next = doc.querySelectorAll(sel);
            for (var i = 0; i < live.length; i++) {
                live[i].innerHTML = next[i].innerHTML;
                // data-gloss on the gloss span is read by landing_garden.js at plant time, so
                // copying the attributes is what makes the NEXT centre flower speak the new
                // language. Cheap enough to do for every region rather than special-casing one.
                copyAttributes(live[i], next[i]);
            }
        });
    }

    // Class and the rest, but never the ones the live page owns: `style` is where a mid-flight
    // animation keeps its state, and nothing in the swapped set is styled inline from the server.
    function copyAttributes(live, next) {
        for (var i = 0; i < next.attributes.length; i++) {
            var attr = next.attributes[i];
            if (attr.name !== "style") { live.setAttribute(attr.name, attr.value); }
        }
    }

    function swapHead(doc) {
        var title = doc.querySelector("title");
        if (title) { document.title = title.textContent; }
        var lang = doc.documentElement.getAttribute("lang");
        if (lang) { document.documentElement.setAttribute("lang", lang); }
        var desc = doc.querySelector('meta[name="description"]');
        var liveDesc = document.querySelector('meta[name="description"]');
        if (desc && liveDesc) { liveDesc.setAttribute("content", desc.getAttribute("content") || ""); }
        // canonical / hreflang / robots are deliberately NOT synced: no crawler ever runs this
        // path (it follows the plain link and gets the server-rendered page with its own tags),
        // and rewriting them here would only ever be able to go stale in a way nothing reads.
    }

    function apply(doc) {
        swapHead(doc);
        swapRegions(doc);
        // The headword rotator is holding the very nodes we just replaced.
        if (window.PL_LANDING_ENTRY) { window.PL_LANDING_ENTRY.restart(); }
        // The new reader language's app bundle (landing_prefetch.js reads it off the CTA block,
        // whose attributes swapRegions just copied).
        if (window.PL_LANDING_PREFETCH) { window.PL_LANDING_PREFETCH.refresh(); }
    }

    // The one visible cue that something happened: the swapped copy dips and comes back. Without
    // it the change is TOO quiet — a click that silently rewrites the words reads as a glitch —
    // and with a full reload's flash gone there is nothing else saying "that worked".
    //
    // ⚠️ The dip is the ONE deliberate delay on this path, so it is measured, not guessed (owner,
    // 2026-08-31: "why does switching langs take 500-1000ms?"). Measured on dev at the time:
    // 40ms server render + 1ms parse + 1ms innerHTML + ~40ms style/layout — and on top of that
    // sat a full 120ms of dip that only STARTED once the document had arrived. Two changes:
    // the dip is 70ms rather than 120, and it now runs CONCURRENTLY with the fetch (see go()),
    // so a cold cache costs max(fetch, dip) instead of fetch + dip. A slow fetch simply holds
    // the card dimmed a little longer, which reads as loading rather than as a stall.
    var FADE_MS = 70;

    /** Dim the card and resolve when the dip has played. Reduced motion: resolves at once. */
    function fadeOut() {
        var card = document.querySelector(".landingCardClass");
        if (!card || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return Promise.resolve(null);
        }
        card.style.transition = "opacity " + (FADE_MS / 1000) + "s ease";
        card.style.opacity = ".45";
        return new Promise(function (resolve) {
            window.setTimeout(function () { resolve(card); }, FADE_MS);
        });
    }

    function fadeIn(card) {
        if (!card) { return; }
        card.style.opacity = "1";
        window.setTimeout(function () { card.style.transition = ""; card.style.opacity = ""; }, 200);
    }

    function load(href) {
        if (cache[href]) { return cache[href]; }
        // Same-origin GET of a public landing: it negotiates nothing, writes nothing and mints
        // no user (that is POST /start's job alone), so this is as cheap as the navigation it
        // replaces. `credentials: same-origin` keeps a signed-in visitor's session cookie on it
        // so the fetched page is the one they would have navigated to.
        cache[href] = fetch(href, { credentials: "same-origin", headers: { "Accept": "text/html" } })
            .then(function (r) {
                if (!r.ok) { throw new Error("HTTP " + r.status); }
                return r.text();
            })
            .then(function (html) {
                return parser.parseFromString(html, "text/html");
            })
            .catch(function (e) {
                delete cache[href];
                throw e;
            });
        return cache[href];
    }

    // Both halves start on the same tick: the document is fetched (or read straight out of the
    // prefetch cache) WHILE the card dips. Whichever finishes last decides when the words move.
    function go(href, push) {
        if (busy) { return; }
        busy = true;
        Promise.all([load(href), fadeOut()]).then(function (both) {
            var doc = both[0];
            var card = both[1];
            if (!sameShape(doc)) {
                window.location.href = href;
                return;
            }
            apply(doc);
            if (push) { history.pushState({ landingLang: true }, "", href); }
            fadeIn(card);
        }).catch(function () {
            // Anything at all — offline, a 404 from a route that changed under us — and the
            // link does what it always did. Never leave the visitor on a page that ignored
            // their click.
            window.location.href = href;
        }).then(function () { busy = false; });
    }

    document.addEventListener("click", function (e) {
        // Modified clicks belong to the browser: ctrl/cmd-click opens a tab, shift a window,
        // and a middle click never reaches a "click" listener as button 0 anyway.
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        var link = e.target.closest ? e.target.closest(SELECTOR) : null;
        if (!link || link.origin !== window.location.origin) { return; }
        if (link.pathname === window.location.pathname) { e.preventDefault(); return; }
        e.preventDefault();
        go(withAttribution(link.pathname + link.search), true);
    });

    // Back/forward across languages the visitor switched through. Same swap, no push — and a
    // state that is not ours (an entry from before this page was loaded) is left to the browser.
    window.addEventListener("popstate", function (e) {
        if (!e.state || !e.state.landingLang) { return; }
        go(window.location.pathname + window.location.search, false);
    });

    // The entry the visitor ARRIVED on has null state, and Back onto it is a same-document
    // navigation: without this the browser would move the URL back to /en while the page stayed
    // in Russian, and nothing would repaint it. Claiming the first entry too makes every step of
    // the language history — including the one back to where they came in — our own swap.
    history.replaceState({ landingLang: true }, "", window.location.href);

    // Pre-warm the cache for every OTHER reader language (owner, 2026-08-31: "can we preload all
    // the langs on first opening to avoid delay on lang change?") — the first pill click then
    // swaps from cache with zero network wait, same as every later one. A failed prefetch costs
    // nothing: go() falls back to load()'s normal fetch-or-navigate. Crawlers see only the plain
    // links and never run this, so the SEO surface is untouched.
    //
    // ⚠️ It used to be requestIdleCallback(…, {timeout: 3000}), and that was the reason the FIRST
    // switch still paid a full round trip: this page never went idle early — the garden animation
    // held a requestAnimationFrame loop from first paint until every plant had bloomed — so in
    // practice the callback fired on its 3-second timeout, and a visitor who read the card and
    // clicked inside those three seconds got a cold cache.
    //
    // ⭐ It now runs on `load` (owner, 2026-09-03). `defer` was not late enough: these four
    // documents are ~4 kB each over the wire but they are still four extra requests competing with
    // the page's OWN critical resources on a phone's connection, and the landing's first paint is
    // now bounded by exactly that pipe rather than by the megabyte of fonts it used to carry
    // ([[seo-readiness-plan]] §10). `load` fires once the page has everything it needs, which is
    // seconds before a human has read the card and chosen a language — so this keeps the owner's
    // 2026-08-31 requirement (no delay on the FIRST switch) while stopping the prefetch from
    // taxing the paint that decides whether the visitor stays at all.
    //
    // ⛔ And the idle route is still wrong, for a reason that has only MOVED: the garden no longer
    // holds a rAF loop from first paint, but it now waits for `requestIdleCallback` itself, so an
    // idle-scheduled prefetch would race the garden's own build for the same quiet moment.
    function afterLoad(fn) {
        if (document.readyState === "complete") { fn(); }
        else { window.addEventListener("load", fn, { once: true }); }
    }

    function prefetch() {
        var links = document.querySelectorAll(SELECTOR);
        var seen = {};
        for (var i = 0; i < links.length; i++) {
            var link = links[i];
            if (link.origin !== window.location.origin) { continue; }
            // Deliberately NOT withAttribution(): see the note above ATTRIBUTION.
            var href = link.pathname + link.search;
            if (link.pathname === window.location.pathname || seen[href]) { continue; }
            seen[href] = true;
            load(href).catch(function () { /* the click path retries for real */ });
        }
    }

    afterLoad(prefetch);
})();
